import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import { Button } from '@/components/atoms/Button';
import { DatePickerRange } from '@/components/atoms/DatePicker';
import Modal from '@/components/atoms/Modal';
import DateIntervalsSection, {
	DateInterval,
} from '@/components/molecules/DateIntervalsSection';
import EmployeeInfoSection from '@/components/molecules/EmployeeInfoSection';
import ExtraTripFormFields from '@/components/molecules/ExtraTripFormFields';
import TransportSection, {
	TransportDay,
} from '@/components/molecules/TransportSection';
import {
	AbsenteeismCategory,
	AbsenteeismCategoryType,
	ExtraTripMotive,
} from '@/constants/ExtraTripConstants';
import {
	ExtraTripFormData,
	ExtraTripFormSchema,
} from '@/constants/formSchema/ExtraTripFormSchema';
import api from '@/services/apiSgft';
import {
	extraTripTodayKey,
	intervalIsOnlyToday,
	shouldLockIntervalTransport,
	transportLockMessage,
} from '@/utils/isEmergencyExtraTripFlow';

interface FormDataResponse {
	employee: {
		name: string;
		employeeNumber: string;
		role: string;
		teamStructure: string;
		workStation: string;
		state: string;
		zipCode: string;
		city: string;
		neighborhood: string;
		street: string;
		houseNumber: string;
		addressDetails: string;
	};
	board: {
		id: number;
		name: string;
	};
	management: {
		id: number;
		name: string;
	};
	teamStructures: Array<{
		id: number;
		name: string;
	}>;
	teams: Array<{
		id: number;
		name: string;
		teamStructureId: number;
		teamStructureName: string;
	}>;
	employees: Array<{
		id: number;
		name: string;
		team: string;
		teamStructure: string;
	}>;
	workStations: Array<{
		id: number;
		name: string;
	}>;
	isEmergencyFlow: boolean;
}

interface AvailableDates {
	availableDatesPeriods: Array<{
		startDate: string;
		endDate: string;
	}>;
}

interface ExtraTripPeriod {
	id: string;
	startDate: string;
	endDate: string;
	entryTime: string;
	exitTime: string;
	location: number | null;
	confinement: boolean;
	needTransport: boolean;
}

interface NewExtraTripModalProps {
	employeeId: string | number;
	modalIsOpen: boolean;
	handleClose: () => void;
	editingId?: string | number | null;
}

const NewExtraTripModal: React.FC<NewExtraTripModalProps> = ({
	employeeId,
	modalIsOpen,
	handleClose,
	editingId,
}) => {
	const fetchModalData = async (): Promise<FormDataResponse> => {
		try {
			const response = await api.get<FormDataResponse>(
				`/extra-trip-solicitation/employee/${employeeId}/form-data`,
			);
			return response.data;
		} catch (error) {
			toast.error('Erro ao carregar dados do modal', {
				theme: 'colored',
				toastId: 'error',
			});
			throw error;
		}
	};

	const { data: formData } = useQuery(
		['extra-trips-form-data', employeeId],
		fetchModalData,
		{
			enabled: !!employeeId && modalIsOpen,
			retry: false,
		},
	);

	const fetchEditingData = async () => {
		if (!editingId) return null;
		try {
			const response = await api.get(`/extra-trip-solicitation/${editingId}`);
			return response.data;
		} catch (error) {
			toast.error('Erro ao carregar dados para edição', {
				theme: 'colored',
				toastId: 'error',
			});
			throw error;
		}
	};

	const { data: editingData } = useQuery(
		['extra-trip-editing', editingId],
		fetchEditingData,
		{
			enabled: !!editingId && modalIsOpen,
			retry: false,
		},
	);

	const defaultValues = useMemo<ExtraTripFormData>(
		() => ({
			motive: ExtraTripMotive.VacancyCovering,
			detail: '',
			category: null,
			coveredEmployee: null,
			coveredEmployeeManagement: '',
			coveredEmployeeStructure: '',
			coveredEmployeeTeam: '',
			vacancy: '',
			team: null,
			teamStructure: null,
			teamManagement: null,
			teamBoard: null,
			dateIntervals: [
				{
					id: '1',
					dateRange: undefined,
					entryTime: '',
					exitTime: '',
					location: null,
					confinement: false,
					needTransport: false,
				},
			],
			transportDays: [],
			repeatLocation: {},
			isTransportExpanded: true,
			passengerPhone: '',
		}),
		[],
	);

	const methods = useForm<ExtraTripFormData>({
		resolver: zodResolver(ExtraTripFormSchema),
		defaultValues,
		mode: 'onChange',
	});

	const { watch, setValue, reset, getValues, handleSubmit, control } = methods;

	const {
		append: appendDateInterval,
		remove: removeDateInterval,
		update: updateDateIntervalField,
	} = useFieldArray({
		control,
		name: 'dateIntervals',
	});

	const motive = watch('motive');
	const dateIntervals = watch('dateIntervals');
	const transportDays = watch('transportDays');
	const isTransportExpanded = watch('isTransportExpanded');

	const detail = watch('detail');
	const category = watch('category');
	const coveredEmployee = watch('coveredEmployee');
	const coveredEmployeeStructure = watch('coveredEmployeeStructure');
	const coveredEmployeeTeam = watch('coveredEmployeeTeam');
	const vacancy = watch('vacancy');
	const team = watch('team');
	const teamStructure = watch('teamStructure');
	const passengerPhone = watch('passengerPhone');
	const repeatLocation = watch('repeatLocation');

	const isEmergencyFlow = formData?.isEmergencyFlow ?? false;
	const hasOnlyTodayInterval = dateIntervals.some((interval) =>
		intervalIsOnlyToday(interval),
	);
	const isIntervalTransportLocked = useCallback(
		(interval: {
			dateRange?: { startDate?: string; endDate?: string };
		}) => !formData || shouldLockIntervalTransport(interval, isEmergencyFlow),
		[formData, isEmergencyFlow],
	);
	const lockNeedTransportMessage = formData
		? transportLockMessage(isEmergencyFlow, hasOnlyTodayInterval)
		: null;

	useEffect(() => {
		if (
			!dateIntervals.some(
				(interval) =>
					interval.needTransport && isIntervalTransportLocked(interval),
			)
		) {
			return;
		}

		setValue(
			'dateIntervals',
			dateIntervals.map((interval) =>
				isIntervalTransportLocked(interval)
					? { ...interval, needTransport: false }
					: interval,
			),
		);
	}, [dateIntervals, isIntervalTransportLocked, setValue]);

	const fetchAvailableDatesPeriods =
		useCallback(async (): Promise<AvailableDates> => {
			try {
				const response = await api.get(
					`/extra-trip-solicitation/employee/${employeeId}/available-dates`,
					{
						params: {
							coveredEmployeeId: coveredEmployee,
							extraTripSolicitationId: editingId
								? Number(editingId)
								: undefined,
						},
					},
				);
				return response.data;
			} catch (error) {
				toast.error('Erro ao carregar datas disponíveis', {
					theme: 'colored',
					toastId: 'error',
				});
				throw error;
			}
		}, [coveredEmployee, employeeId, editingId]);

	const {
		data: availableDatesPeriods,
		isSuccess: isAvailableDatesQuerySuccess,
	} = useQuery(
		['available-dates-periods', coveredEmployee],
		fetchAvailableDatesPeriods,
		{
			enabled: !!employeeId && modalIsOpen,
			retry: false,
		},
	);

	const resetModal = useCallback(() => {
		reset(defaultValues);
	}, [reset, defaultValues]);

	useEffect(() => {
		if (!modalIsOpen) {
			resetModal();
		}
	}, [modalIsOpen, resetModal]);

	useEffect(() => {
		if (editingData && formData) {
			const data = editingData;
			const editingFormData: Partial<ExtraTripFormData> = {
				motive: data.motive,
				detail: data.detail || '',
				category: data.categoryAbsenteeism || null,
				coveredEmployee: data.coveredEmployee || null,
				vacancy: data.vacancy || '',
				team: data.team || null,
				teamStructure: data.teamStructure || null,
				teamManagement: data.teamManagement || null,
				teamBoard: data.teamBoard || null,
				passengerPhone: data.solicitationPhoneNumber || '',
			};

			if (data.periods && data.periods.length > 0) {
				const intervals: DateInterval[] = data.periods.map(
					(period: ExtraTripPeriod) => {
						const startDate = period.startDate;
						const endDate = period.endDate;
						return {
							id: period.id,
							dateRange: {
								startDate,
								endDate,
							},
							entryTime: period.entryTime,
							exitTime: period.exitTime,
							location: period.location,
							confinement: period.confinement,
							needTransport: period.needTransport,
						};
					},
				);
				for (const interval of intervals) {
					if (shouldLockIntervalTransport(interval, formData.isEmergencyFlow)) {
						interval.needTransport = false;
					}
				}
				editingFormData.dateIntervals = intervals;

				const allTransportDays: TransportDay[] = [];
				intervals.forEach((interval) => {
					if (
						interval.needTransport &&
						interval.dateRange?.startDate &&
						interval.dateRange?.endDate
					) {
						const startDateStr = interval.dateRange.startDate;
						const endDateStr = interval.dateRange.endDate;

						const startDate = new Date(startDateStr + 'T00:00:00');
						const endDate = new Date(endDateStr + 'T00:00:00');

						for (
							let d = new Date(startDate);
							d <= endDate;
							d.setDate(d.getDate() + 1)
						) {
							const year = d.getFullYear();
							const month = String(d.getMonth() + 1).padStart(2, '0');
							const day = String(d.getDate()).padStart(2, '0');
							const dateStr = `${year}-${month}-${day}`;
							const dayId = `${interval.id}-${dateStr}`;

							const existingTransportDay = data.transportDays?.find(
								(transportDay: { id: string }) => transportDay.id === dayId,
							);

							allTransportDays.push({
								id: dayId,
								date: dateStr,
								entryTime:
									existingTransportDay?.entryTime || interval.entryTime || '',
								exitTime:
									existingTransportDay?.exitTime || interval.exitTime || '',
								transportType: existingTransportDay?.transportType || null,
								location:
									existingTransportDay?.location || interval.location || null,
							});
						}
					}
				});
				editingFormData.transportDays = allTransportDays;
			} else {
				editingFormData.transportDays = [];
			}

			if (
				data.motive === ExtraTripMotive.Absenteeism &&
				data.coveredEmployee &&
				formData
			) {
				const coveredData = formData.employees.find(
					(emp) => emp.id === data.coveredEmployee,
				);
				if (coveredData) {
					editingFormData.coveredEmployeeStructure = coveredData.teamStructure;
					editingFormData.coveredEmployeeTeam = coveredData.team;
				}
			}

			reset({
				...defaultValues,
				...editingFormData,
			});
		}
	}, [editingData, formData, reset, defaultValues]);

	const locations =
		formData?.workStations.map((ws) => ({
			value: ws.id,
			label: ws.name,
		})) ?? [];

	const motives = [
		{ value: ExtraTripMotive.Absenteeism, label: 'Absenteísmo' },
		{ value: ExtraTripMotive.VacancyCovering, label: 'Cobertura de Vaga' },
		{ value: ExtraTripMotive.SpecialServices, label: 'Serviços Especiais' },
	];

	const categories = [
		{ value: AbsenteeismCategory.Justified, label: 'Justificada' },
		{ value: AbsenteeismCategory.NotJustified, label: 'Não justificada' },
	];

	const yesNoOptions = [
		{ value: 1, label: 'Sim' },
		{ value: 0, label: 'Não' },
	];

	const transportTypeOptions = [
		{ value: 'RoundTrip', label: 'Ida e Volta' },
		{ value: 'Departure', label: 'Ida' },
		{ value: 'Return', label: 'Volta' },
		{ value: 'None', label: 'Nenhum' },
	];

	const filteredTeams = useMemo(() => {
		if (!formData) return [];
		if (teamStructure) {
			return formData.teams.filter(
				(t) => t.teamStructureId === Number(teamStructure),
			);
		}
		return formData.teams;
	}, [formData, teamStructure]);

	const employees =
		formData?.employees.map((emp) => ({
			value: emp.id,
			label: emp.name,
		})) ?? [];

	const teamStructures =
		formData?.teamStructures.map((ts) => ({
			value: ts.id,
			label: ts.name,
		})) ?? [];

	const teams = filteredTeams.map((t) => ({
		value: t.id,
		label: t.name,
	}));

	const handleMotiveChange = (value: number | string) => {
		if (value === null) return;
		setValue('motive', value as ExtraTripMotive, { shouldDirty: true });
		setValue('detail', '');
		setValue('category', null);
		setValue('coveredEmployee', null);
		setValue('coveredEmployeeManagement', '');
		setValue('coveredEmployeeStructure', '');
		setValue('coveredEmployeeTeam', '');
		setValue('vacancy', '');
		setValue('team', null);
		setValue('teamStructure', null);
	};

	const handleCoveredEmployeeChange = (value: number | string) => {
		setValue('coveredEmployee', value as number | null);
		const coveredData = formData?.employees.find((emp) => emp.id === value);
		if (coveredData) {
			setValue('coveredEmployeeStructure', coveredData.teamStructure);
			setValue('coveredEmployeeTeam', coveredData.team);
			const coveredTeam = formData?.teams.find(
				(t) => t.name === coveredData.team,
			);
			if (coveredTeam) {
				setValue('team', coveredTeam.id);
				setValue('teamStructure', coveredTeam.teamStructureId);
			}
		}
	};

	const handleTeamStructureChange = (value: number | string) => {
		setValue('teamStructure', value as number | null);

		const currentTeam = getValues('team');
		if (currentTeam) {
			const currentTeamData = formData?.teams.find(
				(t) => t.id === Number(currentTeam),
			);
			if (
				currentTeamData &&
				currentTeamData.teamStructureId !== Number(value)
			) {
				setValue('team', null);
			}
		}
	};

	const handleTeamChange = (value: number | string) => {
		setValue('team', value as number | null);
		const teamData = formData?.teams.find((t) => t.id === value);
		if (teamData) {
			setValue('teamStructure', teamData.teamStructureId);
		}
	};

	const addDateInterval = () => {
		const newInterval: DateInterval = {
			id: Date.now().toString(),
			dateRange: undefined,
			entryTime: '',
			exitTime: '',
			location: null,
			confinement: false,
			needTransport: false,
		};
		appendDateInterval(newInterval);
	};

	const removeDateIntervalById = (id: string) => {
		const currentIntervals = getValues('dateIntervals');
		if (currentIntervals.length > 1) {
			const index = currentIntervals.findIndex(
				(interval) => interval.id === id,
			);
			if (index !== -1) {
				removeDateInterval(index);
			}
		}
	};

	const updateDateInterval = (
		id: string,
		field: keyof DateInterval,
		value: string | number | boolean | DatePickerRange | null | undefined,
	) => {
		const currentIntervals = getValues('dateIntervals');
		const index = currentIntervals.findIndex((interval) => interval.id === id);

		if (index !== -1) {
			if (
				field === 'needTransport' &&
				isIntervalTransportLocked(currentIntervals[index])
			) {
				return;
			}

			const interval = currentIntervals[index];
			const updated = { ...interval, [field]: value };

			if (
				field === 'dateRange' &&
				value &&
				typeof value === 'object' &&
				'startDate' in value
			) {
				updated.entryTime = interval.entryTime || updated.entryTime;
				updated.exitTime = interval.exitTime || updated.exitTime;
				if (isIntervalTransportLocked(updated)) {
					updated.needTransport = false;
				}
			}

			updateDateIntervalField(index, updated);
		}
	};

	useEffect(() => {
		const prevDays = getValues('transportDays');
		const days: TransportDay[] = [];
		dateIntervals.forEach((interval) => {
			if (
				interval.needTransport &&
				interval.dateRange?.startDate &&
				interval.dateRange?.endDate
			) {
				const startDateStr = interval.dateRange.startDate;
				const endDateStr = interval.dateRange.endDate;

				const startDate = new Date(startDateStr + 'T00:00:00');
				const endDate = new Date(endDateStr + 'T00:00:00');

				for (
					let d = new Date(startDate);
					d <= endDate;
					d.setDate(d.getDate() + 1)
				) {
					const year = d.getFullYear();
					const month = String(d.getMonth() + 1).padStart(2, '0');
					const day = String(d.getDate()).padStart(2, '0');
					const dateStr = `${year}-${month}-${day}`;
					const dayId = `${interval.id}-${dateStr}`;

					const existingDay = prevDays.find((td) => td.id === dayId);
					const lockTransportType = dateStr === extraTripTodayKey();

					days.push({
						id: dayId,
						date: dateStr,
						entryTime: existingDay?.entryTime || interval.entryTime || '',
						exitTime: existingDay?.exitTime || interval.exitTime || '',
						transportType: lockTransportType
							? 'None'
							: existingDay?.transportType || null,
						location: existingDay?.location || interval.location || null,
					});
				}
			}
		});

		const currentDayIds = prevDays
			.map((td) => td.id)
			.sort()
			.join(',');
		const newDayIds = days
			.map((d) => d.id)
			.sort()
			.join(',');

		if (currentDayIds === newDayIds && days.length > 0) {
			const updatedDays = prevDays.map((existingDay) => {
				const newDay = days.find((d) => d.id === existingDay.id);
				if (newDay) {
					return {
						...existingDay,
						entryTime: existingDay.entryTime || newDay.entryTime,
						exitTime: existingDay.exitTime || newDay.exitTime,
						location: existingDay.location || newDay.location,
						transportType:
							newDay.date === extraTripTodayKey()
								? 'None'
								: existingDay.transportType,
					};
				}
				return existingDay;
			});
			setValue('transportDays', updatedDays);
		} else {
			setValue('transportDays', days);
		}
	}, [dateIntervals, getValues, setValue]);

	const updateTransportDay = useCallback(
		(id: string, field: keyof TransportDay, value: string | number | null) => {
			const currentDays = getValues('transportDays');
			const updatedDays = currentDays.map((day) => {
				if (day.id !== id) {
					return day;
				}
				if (field === 'transportType' && day.date === extraTripTodayKey()) {
					return { ...day, transportType: 'None' };
				}
				return { ...day, [field]: value };
			});
			setValue('transportDays', updatedDays);
		},
		[getValues, setValue],
	);

	const handleRepeatLocationChange = (dayId: string, checked: boolean) => {
		const currentRepeatLocation = getValues('repeatLocation');
		const newState = {
			...currentRepeatLocation,
			[dayId]: checked,
		};
		setValue('repeatLocation', newState);

		if (checked && transportDays.length > 0) {
			const sourceIndex = transportDays.findIndex((day) => day.id === dayId);
			if (sourceIndex < 0) {
				return;
			}
			const sourceDay = transportDays[sourceIndex];
			transportDays.slice(sourceIndex + 1).forEach((nextDay) => {
				updateTransportDay(nextDay.id, 'entryTime', sourceDay.entryTime);
				updateTransportDay(nextDay.id, 'exitTime', sourceDay.exitTime);
				if (
					sourceDay.transportType &&
					sourceDay.date !== extraTripTodayKey() &&
					nextDay.date !== extraTripTodayKey()
				) {
					updateTransportDay(
						nextDay.id,
						'transportType',
						sourceDay.transportType,
					);
				}
				if (sourceDay.location) {
					updateTransportDay(nextDay.id, 'location', sourceDay.location);
				}
			});
		}
	};

	const handlePassengerPhoneChange = (value: string) => {
		setValue('passengerPhone', value);
	};

	const handleCancel = () => {
		handleClose();
	};

	const onSubmit = async (data: ExtraTripFormData) => {
		try {
			const {
				motive,
				detail,
				category,
				coveredEmployee,
				vacancy,
				team,
				dateIntervals,
				transportDays,
				passengerPhone,
			} = data;

			const intervalTransportLocked = (
				interval: (typeof dateIntervals)[number],
			) =>
				shouldLockIntervalTransport(
					interval,
					formData?.isEmergencyFlow ?? false,
				);

			const extraTripPeriods = dateIntervals.map((interval) => {
				if (!interval.dateRange?.startDate || !interval.dateRange?.endDate) {
					throw new Error('Data de início e fim são obrigatórias');
				}
				if (!interval.entryTime || !interval.exitTime) {
					throw new Error('Horário de entrada e saída são obrigatórios');
				}
				const days: Array<{
					date: Date;
					entryTime: string;
					exitTime: string;
					transportType: string;
					workStationId?: number;
				}> = [];

				const startDateStr = interval.dateRange.startDate;
				const endDateStr = interval.dateRange.endDate;
				const startDate = new Date(startDateStr + 'T00:00:00');
				const endDate = new Date(endDateStr + 'T00:00:00');

				for (
					let d = new Date(startDate);
					d <= endDate;
					d.setDate(d.getDate() + 1)
				) {
					const year = d.getFullYear();
					const month = String(d.getMonth() + 1).padStart(2, '0');
					const day = String(d.getDate()).padStart(2, '0');
					const dateStr = `${year}-${month}-${day}`;
					const transportDay = transportDays.find(
						(td) => td.date === dateStr && td.id.startsWith(interval.id),
					);

					const entryTime = transportDay?.entryTime || interval.entryTime;
					const exitTime = transportDay?.exitTime || interval.exitTime;

					if (!entryTime || !exitTime) {
						throw new Error(
							`Horários obrigatórios não informados para o dia ${dateStr}`,
						);
					}

					const transportType =
						intervalTransportLocked(interval) ||
						dateStr === extraTripTodayKey() ||
						!interval.needTransport ||
						!transportDay?.transportType ||
						typeof transportDay.transportType !== 'string'
							? 'None'
							: transportDay.transportType;

					const dayWorkStationId = transportDay?.location
						? Number(transportDay.location)
						: interval.location
							? Number(interval.location)
							: undefined;

					days.push({
						date: new Date(dateStr),
						entryTime,
						exitTime,
						transportType,
						...(dayWorkStationId && { workStationId: dayWorkStationId }),
					});
				}

				if (!interval.location) {
					throw new Error('Local é obrigatório para todos os períodos');
				}

				return {
					startDate: new Date(interval.dateRange.startDate),
					endDate: new Date(interval.dateRange.endDate),
					entryTime: interval.entryTime,
					exitTime: interval.exitTime,
					confinement: interval.confinement,
					workStationId: Number(interval.location),
					extraTripDays: days,
				};
			});

			const isTransportNeeded = dateIntervals.some(
				(interval) =>
					interval.needTransport && !intervalTransportLocked(interval),
			);

			const basePayload = {
				isTransportationNeeded: isTransportNeeded,
				solicitationPhoneNumber: passengerPhone || '',
				category: motive || ExtraTripMotive.SpecialServices,
				extraTripPeriods,
				employeeId: undefined as number | undefined,
				absenteeismExtraTrip: undefined as unknown,
				vacancyCoveringExtraTrip: undefined as unknown,
				specialServicesExtraTrip: undefined as unknown,
			};

			if (!editingId) {
				basePayload.employeeId = Number(employeeId);
			}

			const payload = basePayload;

			if (motive === ExtraTripMotive.Absenteeism && coveredEmployee) {
				payload.absenteeismExtraTrip = {
					detailing: detail || '',
					category: category || AbsenteeismCategory.NotJustified,
					coveredEmployeeId: Number(coveredEmployee),
				};
			} else if (motive === ExtraTripMotive.VacancyCovering && team) {
				payload.vacancyCoveringExtraTrip = {
					job: vacancy || '',
					teamId: Number(team),
				};
			} else if (motive === ExtraTripMotive.SpecialServices) {
				payload.specialServicesExtraTrip = {
					detailing: detail || '',
				};
			}

			if (editingId) {
				await api.patch(`/extra-trip-solicitation/${editingId}`, payload);
				toast.success('Ida extra atualizada com sucesso', {
					theme: 'colored',
					toastId: 'success',
				});
			} else {
				await api.post('/extra-trip-solicitation/complete', payload);
				toast.success('Ida extra criada com sucesso', {
					theme: 'colored',
					toastId: 'success',
				});
			}
			resetModal();
			handleClose();
		} catch (err) {
			const error = err as {
				response?: { data: { message: string | string[] } };
				message?: string;
			};
			let errorMessage = 'Erro ao criar ida extra';

			if (error?.response?.data?.message) {
				const message = error.response.data.message;
				errorMessage = Array.isArray(message) ? message.join(', ') : message;
			} else if (
				error?.response?.data &&
				typeof error.response.data === 'string'
			) {
				errorMessage = error.response.data;
			} else if (error?.message && !error.message.includes('status code')) {
				errorMessage = error.message;
			}

			toast.error(errorMessage, {
				theme: 'colored',
				toastId: 'error',
			});
		}
	};

	const handleAdd = handleSubmit(onSubmit, (errors) => {
		const firstError = Object.values(errors)[0];
		if (firstError?.message) {
			toast.error(firstError.message as string, {
				theme: 'colored',
				toastId: 'error',
			});
		}
	});

	const employeeData = {
		name: formData?.employee.name || '',
		employeeNumber: formData?.employee.employeeNumber || '',
		jobTitle: formData?.employee.role || '',
		workStation: formData?.employee.workStation || '',
		teamStructure: formData?.employee.teamStructure || '',
	};

	const passengerAddress = {
		cep: formData?.employee.zipCode || '',
		uf: formData?.employee.state || '',
		city: formData?.employee.city || '',
		neighborhood: formData?.employee.neighborhood || '',
		street: formData?.employee.street || '',
		number: formData?.employee.houseNumber || '',
		complement: formData?.employee.addressDetails || '',
	};

	const availableDatesPeriodsData =
		availableDatesPeriods?.availableDatesPeriods || null;

	return (
		<Modal
			isOpen={modalIsOpen}
			onClose={handleClose}
			title={editingId ? 'Editar solicitação' : 'Nova solicitação'}
			className="w-7xl max-w-[95vw]"
			hideLine
		>
			<FormProvider {...methods}>
				<div className="custom-scrollbar max-h-[85vh] overflow-y-auto">
					<div className="flex flex-col gap-4 p-4">
						<EmployeeInfoSection employeeData={employeeData} />

						<div className="flex flex-col gap-3">
							<h3 className="text-lg font-semibold text-primary">
								Nova Ida Extra
							</h3>
							<p className="text-sm text-gray-600">
								Para adicionar uma ida extra, preencha os dados abaixo. Em
								seguida, será possível adicionar as datas correspondentes.
							</p>

							<ExtraTripFormFields
								formValues={{
									motive,
									detail,
									category,
									coveredEmployee,
									coveredEmployeeStructure,
									coveredEmployeeTeam,
									vacancy,
									team,
									teamStructure,
									teamBoard: formData?.board?.name || '',
									teamManagement: formData?.management?.name || '',
								}}
								options={{
									motives,
									categories,
									employees,
									teamStructures,
									teams,
								}}
								onMotiveChange={handleMotiveChange}
								onDetailChange={(value) => setValue('detail', value)}
								onCategoryChange={(value) =>
									setValue('category', value as AbsenteeismCategoryType)
								}
								onCoveredEmployeeChange={handleCoveredEmployeeChange}
								onVacancyChange={(value) => setValue('vacancy', value)}
								onTeamChange={handleTeamChange}
								onTeamStructureChange={handleTeamStructureChange}
							/>

							<DateIntervalsSection
								dateIntervals={dateIntervals as DateInterval[]}
								yesNoOptions={yesNoOptions}
								transportTypeOptions={transportTypeOptions}
								locations={locations}
								availableDatesPeriods={availableDatesPeriodsData}
								isAvailableDatesReady={isAvailableDatesQuerySuccess}
								onAddDateInterval={addDateInterval}
								onRemoveDateInterval={removeDateIntervalById}
								onUpdateDateInterval={updateDateInterval}
								isTransportNeeded={dateIntervals.some(
									(interval) => interval.needTransport,
								)}
								isIntervalTransportLocked={isIntervalTransportLocked}
								lockNeedTransportMessage={lockNeedTransportMessage}
							/>

							{dateIntervals.some((interval) => interval.needTransport) && (
								<TransportSection
									transportData={{
										days: transportDays,
										locations,
										transportTypeOptions,
										repeatLocation,
									}}
									isTransportExpanded={isTransportExpanded}
									onTransportExpandedChange={(value) =>
										setValue('isTransportExpanded', value)
									}
									onUpdateTransportDay={updateTransportDay}
									onRepeatLocationChange={handleRepeatLocationChange}
									lockedTransportDate={
										isEmergencyFlow ? undefined : extraTripTodayKey()
									}
									passengerInfo={{
										address: passengerAddress,
										phone: passengerPhone,
										onPhoneChange: handlePassengerPhoneChange,
										phoneRequired: true,
									}}
								/>
							)}
						</div>

						<div className="mt-4 flex justify-between">
							<Button
								type="button"
								onClick={handleCancel}
								className="rounded-full border border-primary bg-white text-primary hover:bg-gray-50"
							>
								Cancelar
							</Button>
							<Button
								type="button"
								onClick={handleAdd}
								className="rounded-full bg-primary text-white hover:bg-primary/90"
							>
								{editingId ? 'Salvar alterações' : 'Adicionar'}
							</Button>
						</div>
					</div>
				</div>
			</FormProvider>
		</Modal>
	);
};

export default NewExtraTripModal;
